/**
 * Groups the Posted cells an employee sees on the Pickup Shift page.
 *
 * Employees only see whether a shift has open positions, not how many: the
 * posted halves of one day with the same role, shift and half collapse into a
 * single slot. Picking a slot requests one of its cells; the manager then places
 * each requester into any other posted cell of that slot from the requests modal.
 */

// Posted halves in display order: the whole shift first, then each half.
const POSTED_HALVES = [
  { half: 'both_halves', postedKey: 'is_both_halves_posted', label: '' },
  { half: 'first_half', postedKey: 'is_first_half_posted', label: ' / 1 half' },
  { half: 'second_half', postedKey: 'is_second_half_posted', label: ' / 2 half' },
];

const getShiftStartHour = (shiftName) => parseInt(shiftName.split('-')[0], 10);

const getHalfIndex = (slot) => POSTED_HALVES.findIndex((item) => item.half === slot.half);

// A posted half can still hold an assignment (e.g. one half of a filled shift was
// posted), and the backend rejects a request for it.
const isHalfFree = (cell, half) => {
  if (half === 'first_half') return !cell.first_user_id && !cell.both_user_id;
  if (half === 'second_half') return !cell.second_user_id && !cell.both_user_id;
  return !cell.first_user_id && !cell.second_user_id && !cell.both_user_id;
};

/**
 * Collapses the posted halves of one day's cells into slots.
 *
 * @param {Array<object>} cells - The day's cells from the available-pickup-shifts endpoint.
 * @param {number} userId - The viewing employee.
 * @returns {Array<object>} - Slots `{ key, role, shiftName, half, label, cells, pickupCell, isRequested }`,
 *   ordered by shift start and then whole shift before halves. `cells` keeps the endpoint order; `pickupCell`
 *   is the cell a pickup request goes to (the first one that looks free, so every request for the slot lands
 *   on the same cell); `isRequested` is true when the employee already applied for that half of any of them.
 */
export const groupPostedSlots = (cells, userId) => {
  const slotsByKey = new Map();

  cells.forEach((cell) => {
    POSTED_HALVES.forEach(({ half, postedKey, label }) => {
      if (!cell[postedKey]) return;

      const key = `${cell.role}|${cell.shift_name}|${half}`;
      if (!slotsByKey.has(key)) {
        slotsByKey.set(key, { key, role: cell.role, shiftName: cell.shift_name, half, label, cells: [], isRequested: false });
      }

      const slot = slotsByKey.get(key);
      slot.cells.push(cell);
      if (cell.assignment_requests?.some((request) => request.user_id === userId && request.type === half)) {
        slot.isRequested = true;
      }
    });
  });

  const slots = [...slotsByKey.values()];
  slots.forEach((slot) => {
    slot.pickupCell = slot.cells.find((cell) => isHalfFree(cell, slot.half)) || slot.cells[0];
  });

  return slots.sort((a, b) => getShiftStartHour(a.shiftName) - getShiftStartHour(b.shiftName) || getHalfIndex(a) - getHalfIndex(b));
};

/**
 * Tells whether a slot is offered to the employee: not when they already applied for it,
 * nor when they already work an overlapping part of that shift.
 *
 * @param {object} slot - A slot from `groupPostedSlots`.
 * @param {Array<object>} [takenShiftsData] - The employee's own shifts that day,
 *   `{ shiftName, isFirstPartTaken, isSecondPartTaken, isBothPartsTaken }`.
 * @returns {boolean}
 */
export const isSlotOpenForUser = (slot, takenShiftsData = []) =>
  !slot.isRequested &&
  !takenShiftsData.some(
    (takenShift) =>
      takenShift.shiftName === slot.shiftName &&
      (takenShift.isBothPartsTaken ||
        (slot.half !== 'second_half' && takenShift.isFirstPartTaken) ||
        (slot.half !== 'first_half' && takenShift.isSecondPartTaken)),
  );
